import { useQuery } from "@tanstack/react-query";
import { ResearchSheet } from "../types";

interface ApiResearchSheet {
  id: number;
  title: string;
  created_at: string;
  start_date: string;
  attachment: { url: string };
}

interface ApiCategory {
  id: number | string;
  name: string;
  label: string;
  research_sheets: ApiResearchSheet[];
}

interface ApiResponse {
  daily?: ApiCategory[];
  weekly?: ApiCategory[];
  monthly?: ApiCategory[];
}

interface RecentSheetsResult {
  sheets: ResearchSheet[];
  // Live reports listed under their mailing list row, newest first, keyed by
  // category id.
  recentReports: Record<string, ResearchSheet[]>;
}

// Categories that list their live reports under the mailing list row, and how
// many. The day-ahead dailies go out the afternoon before the day they cover,
// so once tomorrow's is published today's would otherwise lose its only link.
const RECENT_REPORT_LIMITS: Record<string, number> = {
  "25": Infinity, // Central Banks
  "40": 2, // Daily US Conference Calendar
  "9": 2, // Daily US Earnings Estimates
  "2": 2, // Daily Economic Releases
};

const fetchRecentSheets = async (): Promise<RecentSheetsResult> => {
  const response = await fetch(
    "https://newsquawk.com/research_sheets.json?code=HzoEdUN6bM-UzfaVKkNg"
  );
  const data: ApiResponse = await response.json();

  const sheetsMap: Record<string, ResearchSheet> = {};
  const recentReports: Record<string, ResearchSheet[]> = {};

  ["daily", "weekly", "monthly"].forEach((period) => {
    (data[period as keyof ApiResponse])?.forEach((category) => {
      const researchSheets = category.research_sheets;

      if (researchSheets && researchSheets.length > 0) {
        researchSheets.sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        );
        const latestSheet = researchSheets[0];
        const categoryId = category.id.toString();

        if (!sheetsMap[categoryId]) {
          sheetsMap[categoryId] = {
            id: categoryId,
            title: category.name,
            category: period,
            pdfUrl: "https://newsquawk.com" + latestSheet.attachment.url,
            uploadDate: new Date(
              latestSheet.created_at
            ).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            }),
            eventDate: new Date(
              latestSheet.start_date
            ).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            }),
            label: category.label,
          };
        }

        const recentReportLimit = RECENT_REPORT_LIMITS[categoryId];
        if (recentReportLimit) {
          recentReports[categoryId] = researchSheets
            .slice(0, recentReportLimit)
            .map((sheet) => ({
              id: `${category.id}-${sheet.id}`,
              title: sheet.title,
              category: period,
              pdfUrl: "https://newsquawk.com" + sheet.attachment.url,
              uploadDate: new Date(sheet.created_at).toLocaleDateString(
                "en-GB",
                {
                  day: "numeric",
                  month: "short",
                }
              ),
              eventDate: new Date(sheet.start_date).toLocaleDateString(
                "en-GB",
                {
                  day: "numeric",
                  month: "short",
                }
              ),
              label: category.label,
            }));
        }
      }
    });
  });

  return { sheets: Object.values(sheetsMap), recentReports };
};

export const useRecentSheets = () => {
  return useQuery({
    queryKey: ["recentSheets"],
    queryFn: fetchRecentSheets,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};
